import { useEffect, useRef, useState } from 'react';
import type { IdeaCategory, IdeaCountsView, IdeaView, ObjectiveSummaryView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Plus, ArrowRight } from '@ui/shared/icons';
import { PickerOptionRow } from '@ui/desktop/objective-form/FormPrimitives';
import { CardTitle, EmptyCaption, TodayCard, todayInputStyle } from './primitives';

const IDEA_CATEGORIES: ReadonlyArray<{ readonly value: IdeaCategory; readonly label: string }> = [
  { value: 'product', label: 'Product' },
  { value: 'ritual', label: 'Ritual' },
  { value: 'experiment', label: 'Experiment' },
];

/**
 * Row D (main) — Ideas (R8.4, design/screens/today-desktop-v2.md SS10.1).
 * Each idea renders as its own small nested card. Promote has a SINGLE
 * target (an objective action) — no Priority option, contrast with Brain
 * dump's two-target popover (AC-T12).
 */
export function IdeasCard({
  ideas,
  counts,
  objectives,
  onChanged,
}: {
  readonly ideas: readonly IdeaView[];
  readonly counts: IdeaCountsView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onChanged: () => void;
}) {
  const [composerOpen, setComposerOpen] = useState(false);

  return (
    <TodayCard label="Ideas">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <CardTitle>Ideas</CardTitle>
        <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {counts.createdToday} today · {counts.createdThisMonth} this month
        </span>
      </div>

      <div style={{ marginTop: 'var(--space-3)' }}>
        {ideas.length === 0 && !composerOpen ? (
          <EmptyCaption>No ideas yet — jot down anything worth revisiting.</EmptyCaption>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--space-3)' }}>
            {ideas.map((idea) => (
              <IdeaCard key={idea.ideaId} idea={idea} objectives={objectives} onChanged={onChanged} />
            ))}
          </div>
        )}
      </div>

      {composerOpen ? (
        <IdeaComposer onClose={() => setComposerOpen(false)} onCreated={() => { setComposerOpen(false); onChanged(); }} />
      ) : (
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', minHeight: 'var(--touch-target-min)', color: 'var(--hue-primary)', fontWeight: 600, fontSize: 'var(--type-caption-size)' }}
        >
          <Plus size={14} aria-hidden="true" />
          Add idea
        </button>
      )}
    </TodayCard>
  );
}

function IdeaCard({
  idea,
  objectives,
  onChanged,
}: {
  readonly idea: IdeaView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const categoryLabel = IDEA_CATEGORIES.find((c) => c.value === idea.category)?.label ?? idea.category;

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <span
        style={{
          alignSelf: 'flex-start',
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-secondary)',
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-pill)',
          padding: '2px 10px',
        }}
      >
        {categoryLabel}
      </span>
      <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{idea.text}</p>
      <button
        type="button"
        aria-label={`Promote: ${idea.text}`}
        onClick={() => setOpen(true)}
        style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', color: 'var(--hue-primary)', fontWeight: 600, fontSize: 'var(--type-caption-size)' }}
      >
        Promote <ArrowRight size={14} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={ref}
          role="listbox"
          aria-label={`Promote ${idea.text} to an objective action`}
          style={{
            position: 'absolute',
            zIndex: 41,
            top: '100%',
            right: 0,
            background: 'var(--surface-overlay)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-2)',
            border: '1px solid var(--border-subtle)',
            padding: 'var(--space-2)',
            minWidth: '220px',
            maxHeight: '240px',
            overflowY: 'auto',
          }}
        >
          {objectives.length === 0 ? (
            <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', padding: '0 var(--space-2)' }}>
              No objectives available yet.
            </p>
          ) : (
            objectives.map((o) => (
              <PickerOptionRow
                key={o.objectiveId}
                label={o.title}
                selected={false}
                onSelect={() => void api.promoteIdea(idea.ideaId, o.objectiveId).then(onChanged)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function IdeaComposer({ onClose, onCreated }: { readonly onClose: () => void; readonly onCreated: () => void }) {
  const api = useDomainApi();
  const [text, setText] = useState('');
  const [category, setCategory] = useState<IdeaCategory>('product');

  async function submit() {
    if (text.trim().length === 0) return;
    await api.createIdea(text.trim(), category);
    onCreated();
  }

  return (
    <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <input
        autoFocus
        aria-label="New idea text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="What's the idea?"
        style={todayInputStyle}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <div role="radiogroup" aria-label="Idea category" style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {IDEA_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              role="radio"
              aria-checked={category === c.value}
              onClick={() => setCategory(c.value)}
              style={{
                fontSize: 'var(--type-caption-size)',
                padding: '2px 10px',
                borderRadius: 'var(--radius-pill)',
                background: category === c.value ? 'var(--surface-raised)' : 'transparent',
                color: category === c.value ? 'var(--text-primary)' : 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onClose} style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          style={{
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}
